import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { Collapsible } from '@base-ui/react/collapsible';
import { LuChevronDown } from 'react-icons/lu';

import { cn } from '@/lib/utils';

interface AccordionItemProps {
  children: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

function AccordionItem({
  children,
  defaultOpen = false,
  open,
  onOpenChange,
  className,
}: AccordionItemProps) {
  return (
    <Collapsible.Root
      defaultOpen={defaultOpen}
      open={open}
      onOpenChange={onOpenChange}
    >
      <div data-slot="accordion-item" className={cn(className)}>
        {children}
      </div>
    </Collapsible.Root>
  );
}

function AccordionTrigger({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'button'>) {
  return (
    <Collapsible.Trigger
      data-slot="accordion-trigger"
      className={cn(
        'flex flex-1 w-full items-center justify-between py-4 text-left text-sm font-medium transition-all cursor-pointer [&[data-panel-open]>svg]:rotate-180',
        className,
      )}
      {...props}
    >
      {children}
      <LuChevronDown className="size-6 shrink-0 transition-transform duration-200" />
    </Collapsible.Trigger>
  );
}

function AccordionContent({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'div'>) {
  return (
    <Collapsible.Panel
      data-slot="accordion-content"
      className="overflow-hidden transition-all data-[ending-style]:h-0 data-[starting-style]:h-0"
    >
      <div className={cn('pb-4 pt-0', className)} {...props}>
        {children}
      </div>
    </Collapsible.Panel>
  );
}

export { AccordionItem, AccordionTrigger, AccordionContent };
